import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {GradientColor} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/color.types';
import {LinkTarget} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link_target.types';
import {
  Banner_InfoIcon,
  Banner_Media,
  Banner_SponsoredBrand,
  GetBannerResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/banner/service.types';
import {useHistory} from '@aboutyou/router';
import {
  BannerInfoIconData,
  ContentPopperProps,
  HighlightBanner,
  HighlightBannerData,
  RichText,
  SponsoredBrandBanner,
  SponsoredBrandData,
  SponsoredBrandFollow,
  SponsoredBrandHeader,
} from '@aboutyou/ui-components';
import {useBrandToggleFollow} from '@aboutyou/user';
import React, {FC, MouseEvent, useCallback} from 'react';
import {useAuthenticationPrompt} from '../Authentication/AuthenticationProvider/useAuthenticationPrompt';

/**
 * The banner's glow colours ship as RGBA components (ASFE-1379); the
 * `HighlightBanner` UI applies each colour verbatim inside a CSS radial
 * gradient, so map every component to an `rgba()` string. Alpha defaults to
 * fully opaque when unset, per the schema.
 */
const gradientColorToCss = ({red, green, blue, alpha}: GradientColor): string =>
  `rgba(${red}, ${green}, ${blue}, ${alpha ?? 1})`;

interface PromotionBannerViewProps extends GetBannerResponse {
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  onClick?: VoidFunction;
  className?: string;
  ['data-testid']?: string;
}

type ForwardedViewProps = Omit<PromotionBannerViewProps, 'banner'>;

/**
 * Builds the banner's click handler. The whole banner is the click target and
 * navigates to its single `target` (the destination carried by the gRPC
 * banner); external targets open in a new tab. Any caller-supplied `onClick`
 * (e.g. closing the offcanvas) still runs first. Returns `undefined` when there
 * is nothing to do, so a target-less banner stays non-interactive instead of
 * rendering a dead button. The info icon is a sibling of the banner button, so
 * tapping it opens the info sheet without triggering this navigation.
 */
const useBannerClickHandler = () => {
  const history = useHistory();

  return useCallback(
    (
      target: LinkTarget | undefined,
      onClick: VoidFunction | undefined,
    ): VoidFunction | undefined => {
      if (!target && !onClick) return undefined;

      return () => {
        onClick?.();
        if (!target) return;
        if (target.external) {
          window.open(target.url, '_blank');
        } else {
          history.push(target.url);
        }
      };
    },
    [history],
  );
};

type BannerClickHandlerBuilder = ReturnType<typeof useBannerClickHandler>;

/**
 * Maps the schema's InfoIcon onto the sheet content: display text or a link
 * CTA, plus the icon's own countdown — the sheet must not fall back to the
 * on-banner countdown (ASFE-14355). An unset variant (or a link CTA without a
 * target to open) yields `undefined`, hiding the icon instead of opening an
 * empty sheet (ASFE-14356).
 */
const toInfoIcon = (
  icon: Banner_InfoIcon | undefined,
  buildClickHandler: BannerClickHandlerBuilder,
  onClick: VoidFunction | undefined,
): BannerInfoIconData | undefined => {
  const variant = icon?.variant;
  if (!icon || !variant) return undefined;

  const countdownEndsAt = icon.countdown?.endsAt;

  if (variant.$case === 'displayText') {
    return {
      content: {
        type: 'displayText',
        headline: variant.displayText.headline,
        body: <RichText>{variant.displayText.subline.text}</RichText>,
      },
      countdownEndsAt,
    };
  }

  if (!variant.cta.linkTarget) return undefined;
  const onCtaClick = buildClickHandler(variant.cta.linkTarget, onClick);
  if (!onCtaClick) return undefined;

  return {
    content: {type: 'cta', label: variant.cta.label, onClick: onCtaClick},
    countdownEndsAt,
  };
};

export const PromotionBannerView: FC<PromotionBannerViewProps> = ({
  banner,
  onClick,
  ...viewProps
}) => {
  const buildBannerClickHandler = useBannerClickHandler();
  const variant = banner?.variant;
  if (!variant) return null;

  switch (variant.$case) {
    case 'highlightBanner': {
      const b = variant.highlightBanner;
      const data: HighlightBannerData = {
        headline: b.headline ?? '',
        subline: b.subline,
        foregroundColor: b.colors.foregroundColor.hex,
        backgroundColor: b.colors.backgroundColor.hex,
        cta: b.cta,
        media: b.media ? toMedia(b.media) : undefined,
        gradientColors:
          b.gradientColors.length > 0
            ? b.gradientColors.map(gradientColorToCss)
            : undefined,
        countdown: b.countdown,
        infoIcon: toInfoIcon(b.infoIcon, buildBannerClickHandler, onClick),
        productImages:
          b.productImages.length > 0
            ? b.productImages.map(p => ({src: p.src, alt: p.description}))
            : undefined,
      };
      return (
        <HighlightBanner
          banner={data}
          onClick={buildBannerClickHandler(b.target, onClick)}
          {...viewProps}
        />
      );
    }

    case 'sponsoredBrand':
      return (
        <SponsoredBrandSlot
          data={variant.sponsoredBrand}
          onClick={onClick}
          {...viewProps}
        />
      );
  }
};

interface SponsoredBrandSlotProps extends ForwardedViewProps {
  data: Banner_SponsoredBrand;
}

/**
 * The follow chip needs the brand (now optional in the schema) and is dropped
 * by design in the plain-headline header presentation. The follow handler is
 * a hook, so the chip-less rendering has to branch on component level instead
 * of conditionally calling it.
 */
const SponsoredBrandSlot: FC<SponsoredBrandSlotProps> = ({
  data,
  ...viewProps
}) => {
  const showFollow =
    data.brand !== undefined && data.header?.$case !== 'headline';

  if (data.brand && showFollow) {
    return (
      <FollowableSponsoredBrandSlot
        data={data}
        brand={data.brand}
        {...viewProps}
      />
    );
  }
  return <SponsoredBrandSlotView data={data} {...viewProps} />;
};

const FollowableSponsoredBrandSlot: FC<
  SponsoredBrandSlotProps & {brand: Brand}
> = ({data, brand, ...viewProps}) => {
  const follow = useFollowBrandHandler(brand, data.tracker);
  return <SponsoredBrandSlotView data={data} follow={follow} {...viewProps} />;
};

interface SponsoredBrandSlotViewProps extends SponsoredBrandSlotProps {
  follow?: SponsoredBrandFollow;
}

const SponsoredBrandSlotView: FC<SponsoredBrandSlotViewProps> = ({
  data,
  follow,
  onClick,
  ...viewProps
}) => {
  const buildBannerClickHandler = useBannerClickHandler();

  const toHeader = (): SponsoredBrandHeader | undefined => {
    const header = data.header;
    if (!header) return undefined;

    if (header.$case === 'headline') {
      return {type: 'headline', text: header.headline};
    }

    const infoIcon = toInfoIcon(
      header.sponsored.infoIcon,
      buildBannerClickHandler,
      onClick,
    );
    // Without valid sheet content there is no disclosure row to open; fall
    // back to the header-less rendering (follow chip only).
    if (!infoIcon) return undefined;

    return {
      type: 'sponsored',
      label: header.sponsored.infoIcon.label,
      infoIcon,
    };
  };

  const banner: SponsoredBrandData = {
    media: toMedia(data.media),
    backgroundColor: data.colors.backgroundColor.hex,
    foregroundColor: data.colors.foregroundColor.hex,
    brand: data.brand
      ? {name: data.brand.name, logoUrl: data.brand.blackLogoImage?.url}
      : undefined,
    productImages:
      data.productImages.length > 0
        ? data.productImages.map(p => ({src: p.src, alt: p.description}))
        : undefined,
    header: toHeader(),
    follow,
  };

  return (
    <SponsoredBrandBanner
      banner={banner}
      onClick={buildBannerClickHandler(data.target, onClick)}
      {...viewProps}
    />
  );
};

const useFollowBrandHandler = (
  brand: Brand,
  tracker: Banner_SponsoredBrand['tracker'],
): SponsoredBrandFollow => {
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();

  const {isFollowed, toggleFollow} = useBrandToggleFollow({
    brand: {
      id: brand.id,
      name: brand.name,
      path: brand.path,
    },
    promotionTracker:
      tracker?.$case === 'promotionTracker'
        ? tracker.promotionTracker
        : undefined,
  });

  const onToggle = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      showUserAuthenticationPrompt({
        context: 'follow-brand',
        needsCheckoutSession: false,
        onSuccess: () => {
          // Defer one tick so the auth state has propagated before toggling.
          setTimeout(() => {
            toggleFollow();
          });
        },
      });
    },
    [showUserAuthenticationPrompt, toggleFollow],
  );

  return {isFollowed, onToggle};
};

const toMedia = (
  m: Banner_Media,
): NonNullable<HighlightBannerData['media']> => {
  const v = m.variant;
  if (!v) return {backgroundColor: m.backgroundColor};

  if (v.$case === 'image') {
    return {
      backgroundColor: m.backgroundColor,
      src: {kind: 'image', url: v.image.url, alt: v.image.description},
    };
  }
  return {
    backgroundColor: m.backgroundColor,
    src: {
      kind: 'video',
      sources: v.video.sources,
      poster: v.video.fallbackImage.url,
      alt: v.video.description,
    },
  };
};
